import { createContext, useContext } from 'react';

import { AvailabilityStatus } from 'Modules/MultiStopWizard/Types/AvailabilityStatus';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { CapacityTagDto } from 'Models/Shared/Capacity/CapacityTagDto';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';

export interface LegOptionContextProps {
	// Legacy approach
	wizardData: BookingWizardData;
	trip: TicketsTabTrip;

	// Loading
	isLoading?: boolean;

	// Booking details
	legType: 'departure' | 'return';

	// Selection details
	isSelected?: boolean;
	isSelectable?: boolean;
	isVisuallyDisabled?: boolean;

	// Route details
	departureTime: Date;
	arrivalTime: Date;
	numberOfStopsBetween?: number;

	// Price details
	price?: number;
	hidePrice?: boolean;

	// Space details
	availabilityStatus?: AvailabilityStatus;
	/**
	 * Whether this sailing has nothing left for this booking.
	 *
	 * <p>Separate from `availabilityStatus`, which answers a related but different question and
	 * does not always agree: a sailing can carry `closed` there while the row prints `SOLD OUT`.
	 * This is the one the message is built from.</p>
	 */
	soldOut?: boolean;
	paxSpaces: number;
	cargoSpaces: number;
	/** The same figure before flooring — see `vehicleExact` on `FerryTripAvailability`. */
	cargoSpacesExact?: number;
	cargoSpacesBasedOnType?: 'weight' | 'length';

	capacityTags: CapacityTagDto[];

	/**
	 * Whether each capacity chip names itself as well as counting.
	 *
	 * <p>The old card draws icon and number only, with the name behind a tooltip, because its chips
	 * sit in a narrow strip beside the price. The redesigned ticket card gives the chips a row of
	 * their own, where `Cars 40` is read at a glance and a tooltip is a thing nobody opens.</p>
	 */
	showCapacityNames?: boolean;

	/**
	 * Whether a closed capacity explains itself.
	 *
	 * <p>Travels with `showCapacityNames` because it only has anywhere to go when the names are
	 * drawn — `(closed)` is the text the mark sits beside, and without names the tag shows a dot.</p>
	 */
	explainClosedCapacity?: boolean;

	/**
	 * Whether a capacity chip's tooltip describes its state rather than naming it again.
	 *
	 * <p>On for a reader who can book a full sailing — see `explainState` on the tag itself.</p>
	 */
	explainCapacityState?: boolean;

	// State details
	errorMessage?: string;

	// Waitlist
	isWaitlisted?: boolean;
	waitlistFull?: boolean;
	refreshTrips?: () => void;

	// Closed
	vehicleBookingsClosed: boolean;
	passengerBookingsClosed: boolean;
	freightBookingsClosed: boolean;

	// Actions
	onClick?: () => void;
}

export const LegOptionContext = createContext<LegOptionContextProps | undefined>(undefined);

export const useLegOptionContext = () => {
	return useContext(LegOptionContext);
};
